import React, { useState, FormEvent } from 'react';
import {
  MemberWithUser,
  Role,
  CreateFromMemberPayload,
  ROLE_OPTIONS,
} from '../types/member-roles';

export interface CreateAccountModalProps {
  member: MemberWithUser;
  onSubmit: (data: CreateFromMemberPayload) => Promise<void>;
  onClose: () => void;
  loading: boolean;
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  content: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '28px',
    width: '420px',
    maxWidth: '90vw',
  },
  title: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#1a6b3c',
    marginBottom: '20px',
  },
  field: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: '#333',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box' as const,
  },
  inputDisabled: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box' as const,
    backgroundColor: '#f5f5f5',
    color: '#666',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    fontSize: '14px',
    backgroundColor: '#fff',
    boxSizing: 'border-box' as const,
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '8px 12px',
    borderRadius: '6px',
    marginBottom: '16px',
    fontSize: '13px',
  },
  hint: {
    fontSize: '12px',
    color: '#888',
    marginTop: '4px',
  },
  hintError: {
    fontSize: '12px',
    color: '#d32f2f',
    marginTop: '4px',
  },
  btnRow: {
    display: 'flex',
    gap: '10px',
    marginTop: '16px',
  },
  submitBtn: {
    padding: '10px 20px',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  submitBtnDisabled: {
    padding: '10px 20px',
    backgroundColor: '#a5d6a7',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'not-allowed',
  },
  cancelBtn: {
    padding: '10px 20px',
    backgroundColor: '#f5f5f5',
    color: '#333',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer',
  },
};

/**
 * Validates that a password meets the minimum length requirement (8 characters).
 */
export function validatePassword(password: string): boolean {
  return password.length >= 8;
}

/**
 * Modal dialog for creating a user account linked to an existing member.
 * Pre-fills email and name from member data, collects role and password.
 */
export function CreateAccountModal({
  member,
  onSubmit,
  onClose,
  loading,
}: CreateAccountModalProps) {
  const [role, setRole] = useState<Role>('Attendee');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);

  const fullName = `${member.firstName} ${member.lastName}`;
  const isPasswordValid = validatePassword(password);
  const canSubmit = isPasswordValid && !loading;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);

    if (!isPasswordValid) return;

    setError('');
    try {
      await onSubmit({
        memberId: member.id,
        role,
        password,
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to create account');
    }
  }

  return (
    <div
      style={styles.overlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-account-modal-title"
    >
      <div style={styles.content} onClick={(e) => e.stopPropagation()}>
        <h3 id="create-account-modal-title" style={styles.title}>
          Create Account
        </h3>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={styles.field}>
            <label style={styles.label}>Full Name</label>
            <input
              style={styles.inputDisabled}
              value={fullName}
              disabled
              aria-label="Full name"
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              style={styles.inputDisabled}
              value={member.email}
              disabled
              aria-label="Email"
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="create-account-role">
              Role
            </label>
            <select
              id="create-account-role"
              style={styles.select}
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              disabled={loading}
            >
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="create-account-password">
              Password
            </label>
            <input
              id="create-account-password"
              style={styles.input}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder="Enter password (min. 8 characters)"
              disabled={loading}
              aria-describedby="password-hint"
            />
            {touched && !isPasswordValid && password.length > 0 && (
              <p id="password-hint" style={styles.hintError}>
                Password must be at least 8 characters
              </p>
            )}
            {!touched && (
              <p id="password-hint" style={styles.hint}>
                Minimum 8 characters
              </p>
            )}
            {touched && isPasswordValid && (
              <p id="password-hint" style={styles.hint}>
                ✓ Password meets requirements
              </p>
            )}
          </div>

          <div style={styles.btnRow}>
            <button
              type="submit"
              style={canSubmit ? styles.submitBtn : styles.submitBtnDisabled}
              disabled={!canSubmit}
            >
              {loading ? 'Creating...' : 'Create Account'}
            </button>
            <button
              type="button"
              style={styles.cancelBtn}
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
